import { LabelProps } from "@ptomasroos/react-native-multi-slider";
import { useCallback, useState } from "react";
import { LayoutChangeEvent, useWindowDimensions } from "react-native";

import SliderCustomLabel from "@/components/molecules/SliderCustomLabel";
import MultiSliderView from "./MultiSliderView";

// design-system: ui-view waiver — onLayout width measurement needs RN View; the web Box atom renders a div and cannot fire onLayout
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: onLayout width measurement on the root primitive. The Box atom forwards onLayout at runtime but its web-facing public type…
import { View } from "@/components/ui/view";
import { CurrencyCode } from "@/generated/graphql";
import formatCurrencyShort from "@/utils/formatCurrencyShort";
import { useDebouncedCallback } from "use-debounce";

/**
 *
 */
export interface MultiSliderProps {
  value: number[];
  minValue: number;
  maxValue: number;
  step?: number;
  onChange?: (value: [number, number]) => void;
  isCurrency?: boolean;
  useLogScale?: boolean;
  isDecimal?: boolean;
  /** When true, hides value labels below the slider and removes negative margin */
  hideLabels?: boolean;
}

/**
 * Container component for dual-handle range slider with custom formatting.
 * Handles layout measurements, value formatting, and label rendering logic.
 * @param root0 - Component props
 * @param root0.value - Current slider values as [min, max] array
 * @param root0.minValue - Minimum allowed value for slider range
 * @param root0.maxValue - Maximum allowed value for slider range
 * @param root0.step - Step increment for slider values
 * @param root0.onChange - Callback invoked when slider values change with new [min, max] tuple
 * @param root0.isCurrency - Whether to format values as currency with EUR symbol
 * @param root0.useLogScale - Whether to use logarithmic scale (powers values by 1/0.3)
 * @param root0.isDecimal - Whether to display values with one decimal place
 * @param root0.hideLabels - When true, hides value labels and removes negative margin offset
 */
const MultiSliderContainer = ({
  value,
  minValue,
  maxValue,
  step,
  onChange,
  isCurrency,
  useLogScale,
  isDecimal,
  hideLabels,
}: MultiSliderProps) => {
  const padding = hideLabels ? 0 : 40;
  const { width: deviceWidth } = useWindowDimensions();
  const [width, setWidth] = useState(deviceWidth - padding);

  const handleChange = useCallback(
    (newValue: [number, number]) => onChange?.(newValue),
    [onChange]
  );

  const debouncedHandleChange = useDebouncedCallback(handleChange, 500, {
    leading: true,
  });

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width),
    []
  );

  const renderCustomLabel = useCallback(
    ({
      oneMarkerValue,
      twoMarkerValue,
      // The thumb offsets the slider measured for this frame. Forwarded so each
      // value can sit centred under its own ball instead of at the track's end.
      oneMarkerLeftPosition,
      twoMarkerLeftPosition,
    }: LabelProps) => {
      const formatValue = (value: number): string => {
        if (useLogScale) {
          const valueFormat = Math.floor(Math.pow(value, 1 / 0.3));
          return isCurrency
            ? formatCurrencyShort(Math.floor(valueFormat), CurrencyCode.Eur)
            : String(Math.floor(valueFormat));
        }
        const valueDecimal = isDecimal
          ? value.toFixed(1)
          : String(Math.floor(value));
        return isCurrency
          ? formatCurrencyShort(Math.floor(value), CurrencyCode.Eur)
          : valueDecimal;
      };

      return (
        <SliderCustomLabel
          oneMarkerValue={formatValue(Number(oneMarkerValue))}
          twoMarkerValue={
            twoMarkerValue !== undefined
              ? formatValue(Number(twoMarkerValue))
              : undefined
          }
          oneMarkerLeftPosition={oneMarkerLeftPosition}
          twoMarkerLeftPosition={twoMarkerLeftPosition}
        />
      );
    },
    [isCurrency, isDecimal, useLogScale]
  );

  return (
    <View className={hideLabels ? "" : "-mt-6"} onLayout={onLayout}>
      <MultiSliderView
        value={value}
        onChange={debouncedHandleChange}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        width={width}
        renderCustomLabel={hideLabels ? undefined : renderCustomLabel}
        useLogScale={useLogScale}
      />
    </View>
  );
};

export default MultiSliderContainer;
